import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, ArrowRightIcon, StarIcon, ShieldCheckIcon, CheckIcon } from 'lucide-react';
import { Emblem } from './Emblem';
export function Hero() {
  return <section className="relative warm-bg overflow-hidden" data-id="element-348">
      {/* Refined social-proof bar — warm, not harsh */}
      <div className="bg-paper-deep/60 border-b border-gold/25" data-id="element-349">
        <div className="container-custom py-2.5 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-1 text-center" data-id="element-350">
          <div className="flex items-center gap-2" data-id="element-351">
            <div className="flex gap-0.5" data-id="element-352">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3.5 h-3.5 text-gold-dark fill-gold-dark" data-id="element-353" />)}
            </div>
            <span className="text-xs text-primary font-medium" data-id="element-354">
              5.0 on Google
            </span>
          </div>
          <span className="hidden sm:inline text-gold-dark/40" data-id="element-355">·</span>
          <span className="text-xs text-secondary" data-id="element-356">
            75+ years of Perth families · 1947–2026
          </span>
          <span className="hidden md:inline text-gold-dark/40" data-id="element-357">·</span>
          <span className="hidden md:inline text-xs text-gold-dark font-medium" data-id="element-358">
            Replies within 1 working day, personally
          </span>
        </div>
      </div>

      <div className="container-custom pt-12 md:pt-16 pb-16 md:pb-24" data-id="element-359">
        {/* The brand promise — emotional anchor */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="text-center mb-14 md:mb-16" data-id="element-360">
          <div className="flex items-center justify-center gap-4 mb-5" data-id="element-361">
            <span className="h-px w-12 md:w-20 bg-gold/40" data-id="element-362" />
            <Emblem size={20} className="text-gold" data-id="element-363" />
            <span className="h-px w-12 md:w-20 bg-gold/40" data-id="element-364" />
          </div>
          <p className="font-serif text-xl md:text-[1.75rem] text-gold-dark italic tracking-wide leading-tight" data-id="element-365">
            Heritage.
            <span className="text-gold/60 not-italic font-normal mx-3" data-id="element-366">·</span>
            Craftsmanship.
            <span className="text-gold/60 not-italic font-normal mx-3" data-id="element-367">·</span>
            Trust.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-id="element-368">
          {/* Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15
        }} className="lg:col-span-7" data-id="element-369">
            <h1 className="font-serif text-[2.5rem] sm:text-5xl lg:text-[4rem] xl:text-[4.5rem] font-medium leading-[1.02] tracking-tight mb-7 text-balance" data-id="element-370">
              The ring she will wear
              <span className="block italic text-gold-dark mt-0.5" data-id="element-371">
                forever,
              </span>
              <span className="block" data-id="element-372">handcrafted in Perth —</span>
              <span className="block text-3xl sm:text-4xl lg:text-5xl text-secondary font-normal italic mt-3" data-id="element-373">
                by the same family since 1947.
              </span>
            </h1>

            <p className="text-lg text-secondary leading-relaxed mb-8 max-w-2xl text-pretty" data-id="element-374">
              Custom engagement rings, heirloom remodelling and trusted repairs
              — designed, made and crafted on our bench in Ardross by{' '}
              <span className="text-primary font-medium" data-id="element-375">
                Sinclair's Jewellers
              </span>
              , now with the third generation of the same family jewellers.
            </p>

            {/* Risk-reversal trust bullets */}
            <ul className="space-y-2.5 mb-9 max-w-xl" data-id="element-376">
              <li className="flex items-start gap-3 text-sm text-primary" data-id="element-377">
                <CheckIcon className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" strokeWidth={3} data-id="element-378" />
                <span data-id="element-379">
                  <strong className="font-semibold" data-id="element-380">
                    Custom engagement rings
                  </strong>{' '}
                  — firm written quote, before any work begins
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-primary" data-id="element-381">
                <CheckIcon className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" strokeWidth={3} data-id="element-382" />
                <span data-id="element-383">
                  <strong className="font-semibold" data-id="element-384">
                    Handcrafted in our Ardross workshop
                  </strong>{' '}
                  — you can see the jewellers at work
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-primary" data-id="element-385">
                <CheckIcon className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" strokeWidth={3} data-id="element-386" />
                <span data-id="element-387">
                  <strong className="font-semibold" data-id="element-388">
                    Lifetime cleaning &amp; inspection
                  </strong>{' '}
                  — included on every piece we make, forever
                </span>
              </li>
            </ul>

            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5" data-id="element-389">
              <a href="#consultation" className="btn-primary group text-base" data-id="element-390">
                Book your private consultation
                <ArrowRightIcon className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" data-id="element-391" />
              </a>
              <a href="tel:0893162321" className="btn-secondary text-base" data-id="element-392">
                <PhoneIcon className="w-4 h-4 mr-2 text-gold" data-id="element-393" />
                08 9316 2321
              </a>
            </div>

            {/* Micro-trust beneath CTAs */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted" data-id="element-394">
              <span className="inline-flex items-center gap-1.5" data-id="element-395">
                <ShieldCheckIcon className="w-3.5 h-3.5 text-gold" data-id="element-396" />
                Free · No obligation
              </span>
              <span className="text-muted/40" data-id="element-397">·</span>
              <span data-id="element-398">No sales pressure, ever</span>
              <span className="text-muted/40" data-id="element-399">·</span>
              <span className="text-gold-dark font-medium italic" data-id="element-400">
                "The kettle's always on."
              </span>
            </div>
          </motion.div>

          {/* Hero imagery */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.1,
          delay: 0.25
        }} className="lg:col-span-5 relative" data-id="element-401">
            <div className="relative" data-id="element-402">
              {/* Main circle — workshop hands */}
              <div className="relative aspect-square w-full max-w-[480px] mx-auto rounded-full overflow-hidden border-[6px] border-paper shadow-[0_30px_60px_-15px_rgba(31,27,23,0.25)]" data-id="element-403">
                <img src="https://cdn.magicpatterns.com/uploads/iPJgC3uU24qy6g4DSNZ3QQ/sn2.webp" alt="A Sinclair's jeweller at the bench, finishing a hand-set ring in the Ardross workshop" className="absolute inset-0 w-full h-full object-cover" data-id="element-404" />
              </div>

              {/* Floating product showcase — bottom right */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 0.7
            }} className="absolute -bottom-6 -right-2 md:-right-6 w-32 md:w-44 aspect-square rounded-full overflow-hidden border-[5px] border-background shadow-xl bg-surface" data-id="element-405">
                <img src="https://cdn.magicpatterns.com/uploads/i97S84Xv1qcHKn7jhaikdm/d2.webp" alt="A Sinclair's-made marquise diamond engagement ring in yellow gold with pavé band" className="absolute inset-0 w-full h-full object-cover scale-110" data-id="element-406" />
              </motion.div>

              {/* Floating quote — top left */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: 0.6
            }} className="hidden md:block absolute -top-4 -left-6 bg-background px-5 py-4 border-l-2 border-gold max-w-[240px] shadow-lg" data-id="element-407">
                <p className="font-serif italic text-primary text-base leading-snug" data-id="element-408">
                  "Every piece passes through the hands of our own jewellers,
                  here in Ardross."
                </p>
                <p className="brand-mark text-[0.6rem] uppercase text-muted mt-2" data-id="element-409">
                  James Sinclair · Principal
                </p>
              </motion.div>
            </div>

            {/* Stat strip beneath */}
            <div className="mt-10 grid grid-cols-3 gap-px bg-gold/25 border border-gold/25 max-w-md mx-auto" data-id="element-410">
              <div className="bg-background py-5 px-2 text-center" data-id="element-411">
                <p className="font-brand text-2xl md:text-3xl text-gold-dark font-medium" data-id="element-412">
                  75+
                </p>
                <p className="brand-mark text-[0.6rem] uppercase text-muted mt-1.5" data-id="element-413">
                  Years trading
                </p>
              </div>
              <div className="bg-background py-5 px-2 text-center" data-id="element-414">
                <p className="font-brand text-2xl md:text-3xl text-gold-dark font-medium" data-id="element-415">
                  3
                </p>
                <p className="brand-mark text-[0.6rem] uppercase text-muted mt-1.5" data-id="element-416">
                  Generations
                </p>
              </div>
              <div className="bg-background py-5 px-2 text-center" data-id="element-417">
                <p className="font-brand text-2xl md:text-3xl text-gold-dark font-medium" data-id="element-418">
                  5.0
                </p>
                <p className="brand-mark text-[0.6rem] uppercase text-muted mt-1.5" data-id="element-419">
                  Google rating
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Conversion ribbon */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="mt-16 md:mt-20 pt-10 border-t border-gold/20" data-id="element-420">
          <p className="text-center brand-mark text-[0.65rem] uppercase text-muted mb-6" data-id="element-421">
            Or jump straight to what you came for
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-422">
            <a href="#engagement-rings" className="group flex items-center justify-between p-4 bg-surface border border-gold/15 hover:border-gold/40 hover:bg-paper transition-all" data-id="element-423">
              <span className="font-serif text-base text-primary" data-id="element-424">
                Engagement Rings
              </span>
              <ArrowRightIcon className="w-4 h-4 text-gold-dark transition-transform group-hover:translate-x-1" data-id="element-425" />
            </a>
            <a href="#repairs" className="group flex items-center justify-between p-4 bg-surface border border-gold/15 hover:border-gold/40 hover:bg-paper transition-all" data-id="element-426">
              <span className="font-serif text-base text-primary" data-id="element-427">
                Heirloom Remodels
              </span>
              <ArrowRightIcon className="w-4 h-4 text-gold-dark transition-transform group-hover:translate-x-1" data-id="element-428" />
            </a>
            <a href="#repairs" className="group flex items-center justify-between p-4 bg-surface border border-gold/15 hover:border-gold/40 hover:bg-paper transition-all" data-id="element-429">
              <span className="font-serif text-base text-primary" data-id="element-430">
                Repairs · 7 days
              </span>
              <ArrowRightIcon className="w-4 h-4 text-gold-dark transition-transform group-hover:translate-x-1" data-id="element-431" />
            </a>
            <a href="#diamonds" className="group flex items-center justify-between p-4 bg-surface border border-gold/15 hover:border-gold/40 hover:bg-paper transition-all" data-id="element-432">
              <span className="font-serif text-base text-primary" data-id="element-433">
                Diamonds · GIA
              </span>
              <ArrowRightIcon className="w-4 h-4 text-gold-dark transition-transform group-hover:translate-x-1" data-id="element-434" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}